import { useCallback, useMemo } from "react";
import type { ManualArElement, ManualBody } from "@arc/contracts";
import type { AuthSession } from "../../../auth/keycloak.js";
import { useManualBodyMutations } from "../useManualMutations.js";
import { useBodyWriteGate } from "../manual-editor/useBodyWriteGate.js";
import { isRemoteBlur } from "./remote-blur.js";
import type { WebglBridge } from "./bridge.js";

/**
 * The panel's write handlers (ADR-044 §8 Phase 2a) — **the seam every commit funnels through.**
 *
 * Three decisions live here rather than at the controls, because a control is the wrong place to remember any
 * of them:
 *
 * 1. **The remote-blur gate is CENTRAL** (D11). `StepArBlock` has ten `onBlur` handlers, not one; gating the
 *    single control an earlier draft named would have left every other dirty control committing on a Unity
 *    canvas click, with the test passing. One wrapper covers all of them, including controls that do not exist
 *    yet.
 * 2. **The scope fork is read from the TARGET** (D9). A step element and an "Alle Schritte" element have
 *    different endpoints; `locateWebglElement` already says which, so nothing guesses.
 * 3. **One outbound message per CHANGED SETTING** (D12), diffed from the body the write returns — not one per
 *    action. `copyElementSettings` moves N settings in one call, and Unity's only per-setting channel is
 *    `settingChanged`, so one message for N changes leaves it stale for the other N−1.
 */
export interface WebglWrites {
  /** Bound for `bindElementBody`. Undefined while the panel has no session or no manual. */
  onSetSetting?: (elementId: number, name: string, patch: { value?: string | null; fileIds?: number[] }) => void;
  /** Commit a file chosen in the asset picker (D15). Undefined under the same conditions as `onSetSetting`. */
  onPickedFile?: (elementId: number, name: string, mode: "add" | "replace", currentFileIds: number[], fileId: number) => void;
  /** Paste every copyable setting from `sourceElementId` onto `elementId` (D19). */
  onPasteSettings?: (elementId: number, sourceElementId: number) => void;
  /** True while a write this panel issued is in flight — drives the controls' busy state. */
  busy: boolean;
  /** The last write's failure, for the scoped banner. */
  error: unknown;
}

/**
 * Every setting of one element, as a flat map, so two snapshots can be diffed.
 *
 * **The value is a fingerprint, not the raw value (D16).** A file-valued setting carries its payload in `files`,
 * and `value` is usually empty — measured: `XRCModel*` 100 %, `Video` 92 %, `MeshObject` 81 %, `Image` 68 %. A map
 * of values alone therefore cannot SEE a picked image: D12's loop finds nothing moved and sends no
 * `settingChanged`, leaving Unity on the old asset with nothing in the panel looking wrong. That is the exact
 * failure D12 exists to prevent, so the attachment ids are part of what is compared.
 *
 * **First-wins, never last-wins (D21).** Legacy data holds DUPLICATE `(element, setting)` rows — no unique
 * constraint — and the body read returns every one of them, ascending by id, un-deduped. The API writes the
 * LOWEST id and calls it canonical ("so a save can never land on a row the read ignores",
 * `manuals.service.ts:3516`). A `set()` per row keeps the LAST, i.e. the highest id — the row the write never
 * touches — so both snapshots would read it unchanged and a successful save would emit nothing.
 */
function settingsOf(body: ManualBody | undefined, elementId: number): Map<string, string> {
  const out = new Map<string, string>();
  if (!body) return out;
  const all: ManualArElement[] = [
    ...body.allStepsElements,
    ...body.sections.flatMap((s) => s.steps.flatMap((st) => st.elements)),
  ];
  for (const s of all.find((e) => e.id === elementId)?.settings ?? []) {
    if (out.has(s.name)) continue; // D21 — the first row IS the canonical one, because the read is id-ascending
    // File ids are part of the identity, and ORDER matters: `TrainerVideos` holds one video per trainer, so a
    // reorder is a real change Unity must hear about.
    out.set(s.name, `${s.value ?? ""}\u0000${s.files.map((f) => f.fileId).join(",")}`);
  }
  return out;
}

/** The element itself, for the fields the fingerprint deliberately flattens away. */
function elementOf(body: ManualBody, elementId: number): ManualArElement | undefined {
  return [...body.allStepsElements, ...body.sections.flatMap((s) => s.steps.flatMap((st) => st.elements))].find(
    (e) => e.id === elementId,
  );
}


/**
 * The diff baseline for one write, taken INSIDE the gate's serialized op (ADR-053).
 *
 * `previous` is the body the preceding write in the chain committed, version-validated by the gate — so a burst
 * of writes each diff against what the one before them actually left, not against the render-time cache they all
 * closed over. `undefined` means either nothing preceded this write or something else moved the version
 * (a refetch, a reseed), and in both cases the cache IS the fresher truth.
 */
function baselineFor(previous: ManualBody | undefined, cached: ManualBody | undefined, elementId: number): Map<string, string> {
  return settingsOf(previous ?? cached, elementId);
}

export function useWebglWrites(opts: {
  session: AuthSession | null;
  manualId: number | null;
  stepId: number | null;
  body: ManualBody | undefined;
  bridge: WebglBridge;
  /**
   * The file-valued setting names. No longer a deferral list (D13 is closed) — it is what `isFile` is DERIVED
   * from on the outbound message (D17), which is the one thing that must not be guessed per call site.
   */
  fileSettingNames: ReadonlySet<string>;
}): WebglWrites {
  const { session, manualId, stepId, body, bridge, fileSettingNames } = opts;
  const m = useManualBodyMutations(session, manualId);
  const version = body?.manual.version ?? 0;
  const gate = useBodyWriteGate(version);

  const setting = stepId === null ? m.setManualElementSetting : m.setElementSetting;
  /** The clipboard's mutation for THIS scope — surfaced alongside `setting` so a failed paste is never silent. */
  const copy = stepId === null ? m.copyManualElementSettings : m.copyElementSettings;

  /**
   * D12/D18 — the ONE place an outbound `settingChanged` is produced, shared by every write path.
   *
   * One message per setting that ACTUALLY moved, diffed from the body the write returned. A single-setting write
   * emits one; a paste (`copyElementSettings`) moves N and emits N. Writing a second emitter for the paste path
   * would be a second place to forget `isFile`.
   */
  const announce = useCallback(
    (next: ManualBody | void, elementId: number, before: Map<string, string>) => {
      if (!next) return;
      const after = settingsOf(next, elementId);
      const element = elementOf(next, elementId);
      for (const [key, fingerprint] of after) {
        if (before.get(key) === fingerprint) continue;
        const row = element?.settings.find((s) => s.name === key);
        // D17 — DERIVED, never the hardcoded `false` this used to send. Unity branches on the flag: file ⇒
        // re-fetch the element and reload the asset; non-file ⇒ apply `value` directly. Telling it `false` for a
        // picked image hands it an empty string to write into the live scene — worse than silence, because it
        // actively puts the wrong thing there.
        //
        // Derived from the setting's KIND, not from whether it currently holds files. `files.length > 0` reads
        // naturally and is wrong in the one case that matters most: CLEARING an attachment leaves zero files, so
        // it would report `isFile: false` and Unity would apply an empty value instead of dropping the asset —
        // the object stays in the scene.
        const isFile = fileSettingNames.has(key);
        bridge.send({
          type: "settingChanged",
          elementId,
          manualId: next.manual.id,
          settingName: key,
          value: row?.value ?? "",
          isFile,
        });
      }
    },
    [bridge, fileSettingNames],
  );

  const onSetSetting = useCallback(
    (elementId: number, name: string, patch: { value?: string | null; fileIds?: number[] }) => {
      // ── D11. The ONE place a commit can be refused, so no control has to know about Unity at all. ──
      // True only while a Unity-triggered blur is dispatching, which is synchronous — see `remote-blur.ts` for
      // why a flag is sound here when ADR-049 D5 rejects that shape elsewhere.
      if (isRemoteBlur()) return;

      // Assigned by the gate's op below, which runs when this write's TURN comes — not now.
      let before: Map<string, string> | undefined;

      void gate
        .enqueue(setting, (expectedVersion, previous) => {
          before = baselineFor(previous, body, elementId);
          return stepId === null
            ? ({ elementId, name, ...patch, expectedVersion } as never)
            : { stepId, elementId, name, ...patch, expectedVersion };
        })
        .then((next: ManualBody | void) => announce(next, elementId, before ?? settingsOf(body, elementId)))
        // The house pattern every sibling gate chain uses (`ManualEditor.tsx`). `gate.enqueue` REJECTS on a failed
        // write — a CAS 409 from a concurrent edit in another tab or from Unity, a network error — and a `.then`
        // with no rejection handler derives a promise that rejects unhandled, firing `unhandledrejection` on every
        // failed save. The failure is still surfaced: the banner reads `setting.error`, not this chain.
        .catch(() => undefined);
    },
    [body, bridge, fileSettingNames, gate, setting, stepId],
  );

  /**
   * Attach a picked file (Phase 2b, D15). `add` KEEPS what is already attached — `TrainerVideos` holds one video
   * per trainer and a single-id write replaces the whole list; `replace` is the single-file case.
   */
  const onPickedFile = useCallback(
    (elementId: number, name: string, mode: "add" | "replace", currentFileIds: number[], fileId: number) => {
      if (isRemoteBlur()) return; // D11 — the same central gate; a pick is a commit like any other
      let before: Map<string, string> | undefined;
      const fileIds = mode === "add" ? [...new Set([...currentFileIds, fileId])] : [fileId];
      void gate
        .enqueue(setting, (expectedVersion, previous) => {
          before = baselineFor(previous, body, elementId);
          return stepId === null
            ? ({ elementId, name, fileIds, expectedVersion } as never)
            : { stepId, elementId, name, fileIds, expectedVersion };
        })
        .then((next: ManualBody | void) => announce(next, elementId, before ?? settingsOf(body, elementId)))
        .catch(() => undefined);
    },
    [announce, body, gate, setting, stepId],
  );

  /**
   * Paste every copyable setting from another element (Phase 2b, D19).
   *
   * The hazard D13 named is real and stays: a source with no attachments is a faithful CLEAR, so pasting from an
   * element with no image REMOVES the target's. That is the editor's behaviour too — a faithful copy of an
   * element with no image is an element with no image — and it is only acceptable here because the picker now
   * exists to put one back. It is the whole reason the two shipped together.
   */
  const onPasteSettings = useCallback(
    (elementId: number, sourceElementId: number) => {
      if (isRemoteBlur()) return;
      let before: Map<string, string> | undefined;
      void gate
        // D9 — the scope fork, read from the TARGET, exactly as `onSetSetting` and `onPickedFile` do. The first
        // version of this call skipped it and always used the manual-level twin; the diff panel caught it. The
        // all-steps endpoint resolves through `copyElementSettingsScoped(..., {kind:"allSteps"})`, whose
        // `allStepsElementSql` requires `step_id IS NULL` — so pasting onto ANY step element, which is the
        // overwhelmingly common case, 404s and the whole clipboard is dead for it.
        .enqueue(
          stepId === null ? m.copyManualElementSettings : m.copyElementSettings,
          (expectedVersion, previous) => {
            before = baselineFor(previous, body, elementId);
            return (stepId === null
              ? { elementId, sourceElementId, expectedVersion }
              : { stepId, elementId, sourceElementId, expectedVersion }) as never;
          },
        )
        // D18 — the SAME emitter. A paste moves N settings and must emit N messages, which is only automatic
        // because both paths diff the body the write returned.
        .then((next: ManualBody | void) => announce(next, elementId, before ?? settingsOf(body, elementId)))
        .catch(() => undefined);
    },
    [announce, body, gate, m.copyElementSettings, m.copyManualElementSettings, stepId],
  );

  return useMemo(
    () => ({
      onSetSetting: session && manualId !== null ? onSetSetting : undefined,
      onPickedFile: session && manualId !== null ? onPickedFile : undefined,
      onPasteSettings: session && manualId !== null ? onPasteSettings : undefined,
      // EVERY write path this hook owns, not just the single-setting one (diff panel, CONSENSUS). `setting` is
      // the per-setting mutation; the clipboard is a DIFFERENT `useBodyMutation`, so reporting only `setting`
      // left a failed paste completely silent — no banner, no busy state, and the terminal `.catch` swallowing
      // the rejection. The author dismissed the confirm and believed it landed.
      busy: setting.isPending || copy.isPending,
      error: setting.error ?? copy.error,
    }),
    [copy.error, copy.isPending, manualId, onPasteSettings, onPickedFile, onSetSetting, session, setting.error, setting.isPending],
  );
}
